import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  requestEmailSignInAsync,
  requestPhoneSignInAsync,
  requestForEmailAuthenticationVerificationAsync,
  requestForPhoneAuthenticationVerificationAsync,
  requestForRefreshedAuthenticationTokenAsync,
} from "apis/AuthenticationApi";
import { saveAuthInfoToStorage } from "shared/utility/TokenStorageUtility";
import AuthResponse from "interfaces/response/AuthResponse";
import AuthenticationState from "interfaces/state/AuthenticationState";
import EmailAuthenticationVerificationRequest from "interfaces/request/EmailAuthenticationVerificationRequest";
import PhoneAuthenticationVerificationRequest from "interfaces/request/PhoneAuthenticationVerificationRequest";
import EmailSignInRequest from "interfaces/request/EmailSignInRequest";
import PhoneSignInRequest from "interfaces/request/PhoneSignInRequest";

const initialState: AuthenticationState = {
  id: 0,
  email: "",
  expires: "",
  token: "",
  loading: false,
  isAuthenticated: false,
  fullName: "",
  roleName: "",
  role: -1,
  hasApplicationAdminRole: false,
};


export const handlePostSignInOperations = createAsyncThunk(
  "authentication/handlePostSignInOperations",
  async (response: AuthResponse, thunkAPI) => {
    saveAuthInfoToStorage(response);
  }
);

export const setAuthInfo = createAsyncThunk(
  "authentication/setAuthInfo",
  async (authData: AuthResponse, thunkAPI) => {
    return authData;
  }
);

export const emailAuthenticationVerificationRequtestAsync = createAsyncThunk(
  "authentication/emailAuthenticationVerificationRequtestAsync",
  async (postData: EmailAuthenticationVerificationRequest) => {
    return await requestForEmailAuthenticationVerificationAsync(postData);
  }
);

export const phoneAuthenticationVerificationRequtestAsync = createAsyncThunk(
  "authentication/phoneAuthenticationVerificationRequtestAsync",
  async (postData: PhoneAuthenticationVerificationRequest) => {
    return await requestForPhoneAuthenticationVerificationAsync(postData);
  }
);

export const requestForEmailSignInAsync = createAsyncThunk(
  "authentication/emailSignIn",
  async (postData: EmailSignInRequest, { dispatch, rejectWithValue }) => {
    try {
      const response = await requestEmailSignInAsync(postData);
      dispatch(handlePostSignInOperations(response));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const requestForPhoneSignInAsync = createAsyncThunk(
  "authentication/phoneSignIn",
  async (postData: PhoneSignInRequest, { dispatch, rejectWithValue }) => {
    try {
      const response = await requestPhoneSignInAsync(postData);
      dispatch(handlePostSignInOperations(response));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const refreshAuthenticationTokenAsync = createAsyncThunk(
  "authentication/refreshAuthenticationTokenAsync",
  async () => {
    var authResponse = await requestForRefreshedAuthenticationTokenAsync();
    saveAuthInfoToStorage(authResponse);
    return authResponse;
  }
);

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    userLogout: () => {
      saveAuthInfoToStorage(initialState);
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestForEmailSignInAsync.fulfilled, (state, action) => {
      return { ...action.payload, loading: false };
    });
    builder.addCase(requestForEmailSignInAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(requestForEmailSignInAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(requestForPhoneSignInAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(requestForPhoneSignInAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(requestForPhoneSignInAsync.fulfilled, (state, action) => {
      return { ...action.payload, loading: false };
    });
    builder.addCase(setAuthInfo.fulfilled, (state, action) => {
      return { ...action.payload, loading: false };
    });
    builder.addCase(
      emailAuthenticationVerificationRequtestAsync.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      emailAuthenticationVerificationRequtestAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      emailAuthenticationVerificationRequtestAsync.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      phoneAuthenticationVerificationRequtestAsync.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      phoneAuthenticationVerificationRequtestAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      phoneAuthenticationVerificationRequtestAsync.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      refreshAuthenticationTokenAsync.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      refreshAuthenticationTokenAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      refreshAuthenticationTokenAsync.fulfilled,
      (state, action) => {
        return { ...action.payload, loading: false };
      }
    );
  },
});

export const { userLogout } = authenticationSlice.actions;
export default authenticationSlice.reducer;
